import React, { useState, useEffect, useCallback } from 'react';

const STEPS = [
  {
    target: '.metric-cards-grid',
    placement: 'bottom',
    title: 'Live KPI snapshot',
    body: 'These 6 metrics update every morning at 9am ET from real Last.fm data. Days of Data grows daily — richer history means smarter predictions.',
  },
  {
    target: '.tabbar-top',
    placement: 'bottom',
    title: '5 tabs to explore',
    body: 'Anomaly Alerts shows the last 7 days of flagged tracks. Prediction History shows how past signals played out. Cluster Map visualizes all 50 tracks by engagement fingerprint.',
  },
  {
    target: '.alert-card',
    placement: 'top',
    title: 'Anomaly alert cards',
    body: 'This tab shows alerts from the last 7 days. Click any card to see the full 14-day trend. Older alerts with confirmed outcomes live in Prediction History.',
  },
  {
    target: '.tabbar-top',
    placement: 'bottom',
    title: 'Check the predictions 🎯',
    body: 'Head to Prediction History to see how every past alert resolved — did the track climb the chart or stay flat? Real outcomes tracked over 7 days.',
  },
];

export default function TourGuide({ run, onEnd }) {
  const [step, setStep]         = useState(-1);
  const [rect, setRect]         = useState(null);
  const [welcomed, setWelcomed] = useState(false);

  const getRect = useCallback((target) => {
    const el = document.querySelector(target);
    if (!el) return null;
    return el.getBoundingClientRect();
  }, []);

  useEffect(() => {
    if (!run) { setStep(-1); setWelcomed(false); return; }
    setStep(-1);
    setWelcomed(false);
  }, [run]);

  useEffect(() => {
    if (!welcomed || step < 0 || step >= STEPS.length) return;
    const r = getRect(STEPS[step].target);
    setRect(r);
  }, [step, welcomed, getRect]);

  useEffect(() => {
    if (!welcomed || step < 0) return;
    const onScroll = () => {
      const r = getRect(STEPS[step]?.target);
      setRect(r);
    };
    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [step, welcomed, getRect]);

  const handleEnd = () => {
    setStep(-1);
    setWelcomed(false);
    onEnd();
  };

  const handleStart = () => {
    setWelcomed(true);
    setStep(0);
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(s => s + 1);
    } else {
      handleEnd();
    }
  };

  if (!run) return null;

  const PAD  = 8;
  const TT_W = 300;

  function tooltipPosition(r, placement) {
    if (!r) return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' };

    const TT_W = 300;
    const TT_H = 200;
    const MARGIN = 12;

    let top, left;

    if (placement === 'bottom') {
      top  = r.bottom + PAD + 10;
      if (top + TT_H > window.innerHeight - MARGIN) {
        top = r.top - TT_H - PAD - 10;
      }
    } else {
      top = r.top - TT_H - PAD - 10;
      if (top < MARGIN) {
        top = r.bottom + PAD + 10;
      }
    }

    left = r.left + (r.width / 2) - (TT_W / 2);
    left = Math.max(MARGIN, Math.min(left, window.innerWidth - TT_W - MARGIN));

    top = Math.max(MARGIN, Math.min(top, window.innerHeight - TT_H - MARGIN));

    return { position: 'fixed', top, left };
  }

  const overlayStyle = {
    position: 'fixed', inset: 0,
    zIndex: 9000,
    pointerEvents: 'all',
  };

  const spotlightStyle = rect ? {
    position: 'fixed',
    top:    rect.top    - PAD,
    left:   rect.left   - PAD,
    width:  rect.width  + PAD * 2,
    height: rect.height + PAD * 2,
    borderRadius: '8px',
    boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
    border: '2px solid #2DD4BF',
    zIndex: 9001,
    pointerEvents: 'none',
    transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
  } : null;

  const tooltipStyle = {
    ...tooltipPosition(rect, STEPS[step]?.placement),
    zIndex: 9002,
    background: '#161B22',
    border: '1px solid #21262D',
    borderRadius: '14px',
    padding: '20px 22px',
    width: TT_W,
    boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
    animation: 'modalSpringIn 0.3s cubic-bezier(0.34,1.56,0.64,1)',
  };

  const dotStyle = (i) => ({
    width: step === i ? 18 : 6,
    height: 6,
    borderRadius: 3,
    background: step === i ? '#2DD4BF' : '#21262D',
    transition: 'all 0.25s',
    cursor: 'pointer',
  });

  // Welcome modal
  if (!welcomed) {
    return (
      <div style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.7)',
        zIndex: 9000, display: 'flex',
        alignItems: 'center', justifyContent: 'center'
      }}>
        <div style={{
          background: '#1C1C1C', borderRadius: '16px',
          padding: '32px', textAlign: 'center',
          width: 380, border: '1px solid #21262D',
          animation: 'modalSpringIn 0.35s cubic-bezier(0.34,1.56,0.64,1)'
        }}>
          <div style={{
            fontSize: '20px', fontWeight: 700,
            color: '#F9FAFB', marginBottom: '10px',
            letterSpacing: '-0.4px'
          }}>
            Welcome to ChartPulse
          </div>
          <div style={{
            fontSize: '13px', color: '#7D8590',
            lineHeight: 1.6, marginBottom: '18px'
          }}>
            This dashboard detects tracks showing early signs
            of viral momentum — before they blow up on
            streaming platforms.
          </div>
          <div style={{ marginBottom: '20px' }}>
            <span style={{
              padding: '4px 12px', borderRadius: '20px',
              fontSize: '11px', fontWeight: 600,
              background: '#0D2D2A', color: '#2DD4BF',
              border: '1px solid #1A4A46'
            }}>
              Powered by Last.fm data
            </span>
          </div>
          <button
            onClick={handleStart}
            style={{
              width: '100%', padding: '11px',
              background: '#2DD4BF', color: '#0D1117',
              border: 'none', borderRadius: '10px',
              fontSize: '13px', fontWeight: 700,
              cursor: 'pointer', marginBottom: '10px'
            }}
          >
            Show me around →
          </button>
          <button
            onClick={handleEnd}
            style={{
              background: 'none', border: 'none',
              fontSize: '11px', color: '#4B5563',
              cursor: 'pointer'
            }}
          >
            Skip tour
          </button>
        </div>
      </div>
    );
  }

  const currentStep = STEPS[step];
  if (!currentStep) return null;

  return (
    <>
      <div style={overlayStyle} onClick={handleEnd} />
      {spotlightStyle && <div style={spotlightStyle} />}
      <div style={tooltipStyle} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#2DD4BF', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
          Step {step + 1} of {STEPS.length}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#E6EDF3', marginBottom: 8 }}>
          {currentStep.title}
        </div>
        <div style={{ fontSize: 13, color: '#7D8590', lineHeight: 1.6, marginBottom: 18 }}>
          {currentStep.body}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={handleEnd} style={{ background: 'none', border: 'none', fontSize: 12, color: '#4B5563', cursor: 'pointer' }}>
            Skip tour
          </button>
          <div style={{ display: 'flex', gap: 4 }}>
            {STEPS.map((_, i) => (
              <div key={i} style={dotStyle(i)} onClick={() => setStep(i)} />
            ))}
          </div>
          <button onClick={handleNext} style={{ padding: '8px 18px', background: '#2DD4BF', color: '#0D1117', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {step === STEPS.length - 1 ? 'Finish ✓' : 'Next →'}
          </button>
        </div>
      </div>
    </>
  );
}
